import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, Loader2, X, Upload, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import api, { formatApiError } from "../api";
import { BulkImportDialog, downloadMasterExcel } from "./BulkImportDialog";

const fmtDate = (v) => {
  if (!v) return "-";
  const d = new Date(v + "T00:00:00");
  return isNaN(d) ? v : d.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
};

const daysLeft = (v) => Math.ceil((new Date(v + "T00:00:00") - new Date()) / 86400000);

const DateWithWarn = ({ value, warn, testId }) => {
  if (!value) return "-";
  const left = warn ? daysLeft(value) : null;
  return (
    <span className="inline-flex items-center gap-1.5">
      {fmtDate(value)}
      {warn && left !== null && left <= 30 && (
        <span data-testid={testId} className={`text-[10px] font-semibold rounded px-1.5 py-0.5 border ${left < 0 ? "bg-red-100 text-red-800 border-red-200" : "bg-amber-100 text-amber-800 border-amber-200"}`}>
          {left < 0 ? "Kedaluwarsa" : `${left} hari lagi`}
        </span>
      )}
    </span>
  );
};

export const MasterTable = ({ title, icon: Icon, endpoint, fields, columns, statusField, statusBadge, keyField, testPrefix, importEntity }) => {
  const emptyForm = () => Object.fromEntries(fields.map((f) => [f.key, f.default ?? ""]));
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const load = () => api.get(`/${endpoint}`).then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v === "" && fields.find((f) => f.key === k)?.type === "date" ? null : v]));
    try {
      if (editId) {
        await api.put(`/${endpoint}/${editId}`, payload);
        toast.success("Data diperbarui");
      } else {
        await api.post(`/${endpoint}`, payload);
        toast.success("Data ditambahkan");
      }
      setForm(emptyForm());
      setEditId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (item) => {
    setEditId(item.id);
    setForm(Object.fromEntries(fields.map((f) => [f.key, item[f.key] ?? (f.default ?? "")])));
  };

  const remove = async (item) => {
    try {
      await api.delete(`/${endpoint}/${item.id}`);
      toast.success(`${item[keyField]} dihapus`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <Card className="border-zinc-200 shadow-sm">
      <CardHeader className="pb-4 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base font-semibold tracking-tight flex items-center gap-2">
          <Icon className="w-4 h-4 text-emerald-700" /> {title}
        </CardTitle>
        {importEntity && (
          <div className="flex items-center gap-2">
            <Button data-testid={`export-excel-button-${importEntity}`} variant="outline" size="sm"
              onClick={() => downloadMasterExcel(importEntity)}
              className="border-zinc-300 text-zinc-700 hover:bg-zinc-50 transition-colors duration-200 h-8">
              <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" /> Unduh Excel
            </Button>
            <Button data-testid={`bulk-import-button-${importEntity}`} variant="outline" size="sm"
              onClick={() => setImportOpen(true)}
              className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 transition-colors duration-200 h-8">
              <Upload className="w-3.5 h-3.5 mr-1.5" /> Upload CSV/Excel
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-5">
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end bg-zinc-50 border border-zinc-200 rounded-md p-4">
          {fields.map((f) => (
            <div key={f.key} className="space-y-1.5">
              <Label className="text-xs">{f.label}{f.required && " *"}</Label>
              {f.options ? (
                <Select value={form[f.key]} onValueChange={(v) => setForm({ ...form, [f.key]: v })} required={f.required}>
                  <SelectTrigger data-testid={`${testPrefix}-${f.key}`} className="bg-white h-9"><SelectValue placeholder="Pilih" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {f.options.map((o) => <SelectItem key={o} value={o} data-testid={`${testPrefix}-${f.key}-${o.replace(/[ /]/g, "-")}`}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input data-testid={`${testPrefix}-${f.key}`} type={f.type || "text"} required={f.required} placeholder={f.placeholder}
                  value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="bg-white h-9" />
              )}
            </div>
          ))}
          <div className="flex gap-2">
            <Button data-testid={`${testPrefix}-submit`} type="submit" disabled={saving}
              className="bg-emerald-700 hover:bg-emerald-800 text-white transition-colors duration-200 h-9 btn-touch">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editId ? <Pencil className="w-4 h-4 mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />}
              {editId ? "Simpan" : "Tambah"}
            </Button>
            {editId && (
              <Button data-testid={`${testPrefix}-cancel-edit`} type="button" variant="outline" onClick={() => { setEditId(null); setForm(emptyForm()); }} className="h-9 border-zinc-300">
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </form>

        {items === null ? (
          <div className="flex items-center justify-center py-10 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : (
          <div className="overflow-x-auto border border-zinc-200 rounded-md">
            <table className="w-full text-sm" data-testid={`${testPrefix}-table`}>
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  {columns.map((c) => (
                    <th key={c.key} className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-zinc-500 font-medium whitespace-nowrap">{c.label}</th>
                  ))}
                  <th className="px-4 py-2.5 text-xs uppercase tracking-wider text-zinc-500 font-medium text-left">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && <tr><td colSpan={columns.length + 1} className="px-4 py-8 text-center text-zinc-400">Belum ada data</td></tr>}
                {items.map((item) => (
                  <tr key={item.id} data-testid={`${testPrefix}-row-${item[keyField]}`} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors duration-200">
                    {columns.map((c) => (
                      <td key={c.key} className={`px-4 py-2.5 whitespace-nowrap ${c.key === keyField ? "font-medium text-zinc-900" : ""}`}>
                        {c.key === statusField ? (
                          <Badge data-testid={`${testPrefix}-status-${item[keyField]}`} className={`${statusBadge[item[c.key]] || "bg-slate-100 text-slate-700 border border-slate-200"} font-medium`}>
                            {item[c.key]}
                          </Badge>
                        ) : c.date ? (
                          <DateWithWarn value={item[c.key]} warn={c.warn} testId={`${testPrefix}-warn-${c.key}-${item[keyField]}`} />
                        ) : (item[c.key] || "-")}
                      </td>
                    ))}
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <div className="flex gap-1">
                        <button data-testid={`${testPrefix}-edit-${item[keyField]}`} onClick={() => startEdit(item)}
                          className="p-1.5 rounded-md text-zinc-500 hover:text-emerald-700 hover:bg-emerald-50 transition-colors duration-200 icon-touch" title="Edit">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button data-testid={`${testPrefix}-delete-${item[keyField]}`} onClick={() => remove(item)}
                          className="p-1.5 rounded-md text-zinc-500 hover:text-red-600 hover:bg-red-50 transition-colors duration-200 icon-touch" title="Hapus">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      {importEntity && (
        <BulkImportDialog entity={importEntity} open={importOpen} onClose={() => setImportOpen(false)} onDone={load} />
      )}
    </Card>
  );
};
